import React from 'react';
import {
  Box,
  Container,
  Typography,
  Chip,
  Paper,
  useTheme,
} from '@mui/material';
import { motion } from 'framer-motion';
import { TECHNOLOGIES } from '../../utils/constants';

const ExpertiseSection: React.FC = () => {
  const theme = useTheme();

  const categoryNames = {
    frontend: 'Frontend',
    backend: 'Backend',
    cloud: 'Cloud & DevOps',
    data: 'Data & IA',
    security: 'Cybersécurité',
    tools: 'Outils & Méthodologies',
  };

  const categoryColors = {
    frontend: theme.palette.primary.main,
    backend: theme.palette.secondary.main,
    cloud: theme.palette.info.main,
    data: theme.palette.success.main,
    security: theme.palette.error.main,
    tools: theme.palette.warning.main,
  };

  return (
    <Box
      id="expertise"
      sx={{
        py: 10,
        backgroundColor: 'background.paper',
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          <Typography
            variant="h3"
            align="center"
            sx={{
              fontWeight: 700,
              mb: 2,
            }}
          >
            Notre Expertise
          </Typography>
          <Typography
            variant="h6"
            align="center"
            color="text.secondary"
            sx={{ mb: 6, maxWidth: 600, mx: 'auto' }}
          >
            Technologies et méthodologies de pointe pour vos projets
          </Typography>
        </motion.div>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {Object.entries(TECHNOLOGIES).map(([category, techs], index) => (
            <Box key={category}>
              <motion.div
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    backgroundColor: 'background.default',
                    borderLeft: `4px solid ${categoryColors[category as keyof typeof categoryColors]}`,
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      mb: 3,
                      color: categoryColors[category as keyof typeof categoryColors],
                    }}
                  >
                    {categoryNames[category as keyof typeof categoryNames]}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                    {techs.map((tech, techIndex) => (
                      <motion.div
                        key={tech}
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{
                          duration: 0.3,
                          delay: index * 0.1 + techIndex * 0.05,
                        }}
                        viewport={{ once: true }}
                      >
                        <Chip
                          label={tech}
                          sx={{
                            fontSize: '0.95rem',
                            py: 2.5,
                            backgroundColor: `${categoryColors[category as keyof typeof categoryColors]}15`,
                            color: categoryColors[category as keyof typeof categoryColors],
                            border: `1px solid ${categoryColors[category as keyof typeof categoryColors]}30`,
                            '&:hover': {
                              backgroundColor: `${categoryColors[category as keyof typeof categoryColors]}25`,
                              transform: 'translateY(-2px)',
                              transition: 'all 0.2s ease',
                            },
                          }}
                        />
                      </motion.div>
                    ))}
                  </Box>
                </Paper>
              </motion.div>
            </Box>
          ))}
        </Box>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <Box
            sx={{
              mt: 6,
              p: 4,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}10, ${theme.palette.secondary.main}10)`,
              textAlign: 'center',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Méthodologies Agiles
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Nous appliquons les meilleures pratiques Agile et DevOps pour garantir la réussite de vos projets :
              Scrum, Kanban, CI/CD, Infrastructure as Code, et monitoring continu.
            </Typography>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default ExpertiseSection;